import type { SCAPIShopperProductsSchemas } from '@/types';

export const EVENT_INSTANCE_ATTRIBUTE_ID = 'eventInstanceId';

type EventSeriesProduct = Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType' | 'variationAttributes'>;
type EventOccurrence = Pick<SCAPIShopperProductsSchemas['Product'], 'c_eventStartDate' | 'id' | 'inventory'>;

/** Eventbrite series are masters whose sellable occurrences use this variation attribute. */
export function isEventSeriesProduct(product?: EventSeriesProduct | null): boolean {
    const isEventOrCourse = product?.c_productType === 'event' || product?.c_productType === 'course';
    return Boolean(
        isEventOrCourse &&
            product.variationAttributes?.some((attribute) => attribute.id === EVENT_INSTANCE_ATTRIBUTE_ID)
    );
}

function getStartTime(product: EventOccurrence): number {
    if (!product.c_eventStartDate) {
        return Number.POSITIVE_INFINITY;
    }

    const time = new Date(product.c_eventStartDate).getTime();
    return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

/** Keep the occurrence cards and the default selection in chronological order. */
export function sortEventOccurrences<T extends EventOccurrence>(occurrences: T[]): T[] {
    return occurrences
        .map((occurrence, index) => ({ occurrence, index }))
        .sort((a, b) => getStartTime(a.occurrence) - getStartTime(b.occurrence) || a.index - b.index)
        .map(({ occurrence }) => occurrence);
}

/**
 * Select the earliest upcoming orderable occurrence. If all sessions have started,
 * keep the first orderable session, then fall back to the first dated occurrence.
 */
export function getDefaultEventOccurrence<T extends EventOccurrence>(
    occurrences: T[],
    now: Date = new Date()
): T | undefined {
    const sorted = sortEventOccurrences(occurrences);
    const nowTime = Number.isNaN(now.getTime()) ? Date.now() : now.getTime();
    const isOrderable = (occurrence: T) => occurrence.inventory?.orderable !== false;

    return (
        sorted.find((occurrence) => isOrderable(occurrence) && getStartTime(occurrence) >= nowTime) ??
        sorted.find(isOrderable) ??
        sorted[0]
    );
}
